<template>
  <v-card>
    <v-form ref="form" :lazy-validation="true">
      <v-card-title>ÄNDRING AV GRUNDUTBILDNINGSTID</v-card-title>
      <v-card-actions>
        <v-icon class="close" @click="close()">$sl-close</v-icon>
      </v-card-actions>

      <v-card-text class="">
        <v-row no-gutters>
          <v-col cols="3">
            <p class="overline">ÄNDRING GÄLLER</p>
            <p class="mt-n3" v-if="selectedPerson">
              <router-link :to="'/people/' + selectedPerson.id">{{ selectedPerson | fullName }}</router-link>
            </p>
          </v-col>
          <v-col cols="3">
            <p class="overline">PERSONNUMMER</p>
            <p class="mt-n3" v-if="selectedPerson && selectedPerson.personalNumber">
              {{ selectedPerson.personalNumber | formatPnr }}
            </p>
          </v-col>
          <v-col cols="3">
            <p class="overline">MOBILNUMMER</p>
            <p class="mt-n3" v-if="selectedPerson && selectedPerson.mobilephone">
              <a :href="`tel:${selectedPerson.mobilephone}`"> {{ selectedPerson.mobilephone }}</a>
            </p>
          </v-col>
          <v-col cols="3"
            ><p class="overline">E-POSTADRESS</p>
            <p class="mt-n3" v-if="selectedPerson && selectedPerson.email">
              <a :href="`mailto:${selectedPerson.email}`"> {{ selectedPerson.email }}</a>
            </p>
          </v-col>

          <v-col cols="12">
            <p class="overline mt-3">STARTDATUM</p>
            <div class="d-flex">
              <p class="mt-n3" v-if="ETM && ETM.fromDate">
                {{ ETM.fromDate | date }}
              </p>
            </div>
          </v-col>
          <v-col cols="12">
            <p class="overline mt-3">TIDIGARE LÄNGD PÅ GRUNDUTBILDNINGSTID FÖRE ÄNDRING</p>
            <div class="d-flex">
              <p class="mt-n3">
                {{ oldEducationLength }}
                månader
              </p>
            </div>
          </v-col>

          <v-col cols="12">
            <p class="overline mt-3">NY LÄNGD PÅ GRUNDUTBILDNINGSTID EFTER ÄNDRING</p>
            <div class="d-flex">
              <template v-if="ETM && ETM.lengthInMonths" >
                <p class="mt-n3">{{ oldEducationLength + ETM.lengthInMonths }} månader</p>
              </template>
              <template v-else>
                <p class="mt-n3">Avbruten</p>
              </template>
            </div>
          </v-col>
          <v-col cols="6"></v-col>
          <v-col cols="12">
            <p class="overline text-uppercase mt-3">Slutdatum inklusive eventuell förlängning på grund av uppehåll</p>
            <div class="d-flex">
              <p class="mt-n3" v-if="ETM && ETM.toDate">
                {{ finalEndDate | date }}
              </p>
            </div>
          </v-col>

          <v-col cols="6">
            <v-btn
              color="secondary"
              class="icon"
              @click="
                $nextTick(() =>
                  $router
                    .push({name: 'PersonDetail', params: {id: selectedPerson.id}, query: {tab: '4'}})
                    .catch(() => {})
                )
              "
            >
              <v-icon>$sl-stairs-ascend</v-icon>Visa personens grundutbildningstid
            </v-btn>
          </v-col>
          <v-col cols="6">
            <p class="overline text-uppercase mt-3">
              <v-icon class="special-icon-size mr-1">$sl-info</v-icon
              ><a
                href="https://www.byggbranschensyrkesnamnd.se/om-byn/yrkesutbildningsavtal/yua-2020-avtal-och-anvisningar/"
                target="blank"
                >Beräkning av grundutbildningstid</a
              >
            </p>
          </v-col>
        </v-row>
      </v-card-text>

      <v-card-text>
        <v-row>
          <v-col cols="6"
            ><p class="overline text-uppercase mt-3">Godkänd av (1)</p>

            <p class="mt-n3" v-if="ETM && ETM.approvedBy">
              {{ ETM.approvedBy | fullName }}
              <span v-if="ETM.approvedAt">({{ ETM.issuedAt | date }})</span>
            </p>
            <p class="mt-n3 grey-text font-italic" v-else>Ej godkänd.</p>
          </v-col>
          <v-col cols="6"
            ><p class="overline mt-3 text-uppercase">Godkänd av (2)</p>
            <p class="mt-n3" v-if="ETM && ETM.approvedBySecondary">
              {{ ETM.approvedBySecondary | fullName }}
              <span v-if="ETM.approvedAtSecondary">({{ ETM.approvedAtSecondary | date }})</span>
            </p>
            <p class="mt-n3 grey-text font-italic" v-else>Ej godkänd.</p>
          </v-col>
        </v-row>
      </v-card-text>
      <v-card-actions class="justify-end mt-2">
        <v-btn color="secondary" @click="close()"> <v-icon>$sl-cancel</v-icon>Avbryt </v-btn>
        <v-btn outlined
          @click="deleteETM()"
          :disabled="ETM.extensionOrders.some(it => it.state.state === 'approvedByCompany') || !canUpdate"
        >
          <v-icon>$sl-delete</v-icon>Radera
        </v-btn>
        <v-btn color="secondary" 
          @click="deny()"
          :disabled="isButtonsDisabled || isEditMode || !canUpdate"
        >
          <v-icon>$sl-dislike</v-icon>Neka
        </v-btn>
        <v-btn v-if="isEditMode == true"
          @click="update()" 
          :disabled="!canUpdate"
        >
          <v-icon>$sl-check</v-icon>Spara och Godkänn
        </v-btn>
        <v-btn v-else
          @click="approve()" 
          :disabled="isButtonsDisabled || !canUpdate" 
        >
          <v-icon>$sl-like</v-icon>Godkänn
        </v-btn>
      </v-card-actions>
      <p v-if="isSameUserApproved" class="info-text mt-4 pr-10">
        <i
          ><b>Obs:</b> Du har godkänt den här anmälan. Det krävs att en annan regionär kontragodkänner för att anmälan
          ska godkännas fullständigt.</i
        >
      </p>
    </v-form>
  </v-card>
</template>

<script>
import API from '@/services/api.js'
import {mapGetters, mapActions} from 'vuex'
import {Regions, RulesMixin, UserRules, CanIDo} from '@/components/mixins'
import get from 'lodash/get'

export default {
  mixins: [Regions, RulesMixin, UserRules, CanIDo],
  props: {
    // Person object with a default value
    ETM: {
      type: Object,
      // Object or array defaults must be returned from
      // default empty
      default: function () {
        return {}
      }
    },
    approvedExtensions: {
      type: Array,
      default: [],
    },
    paused: {
      type: Array,
      // Object or array defaults must be returned from
      // default empty
      default: function () {
        return []
      }
    },
    isVisible: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      isDisabled: false,
      editEducationTimeLength: false,
      oldQualificationLength: false,
      basicEducationLength: '',
      timesLengths: [],
      maxExtendRange: 6,
      maxExtendCount: 2
    }
  },
  watch: {
    ETM() {
      this.init()
    },
  },

  created() {
    this.init()
  },
  computed: {
    ...mapGetters('person', ['selectedPerson']),
    ...mapGetters('basic', ['getQualificationLengthItems']),
    ...mapGetters('currentuser', ['currentUserRole', 'currentUserId']),

    isExtendEducationModal() {
      return this.ETM.actions === 'extendEducationModalVisible'
    },

    isButtonsDisabled() {
      let approvedById = null
      if (this.ETM && this.ETM.approvedById) {
        approvedById = this.ETM.approvedById
      }

      if (
        !this.canUserApprove(this.ETM.regionId, approvedById) ||
        (this.ETM && this.ETM.approved == true) ||
        (this.ETM && this.ETM.rejectedById && this.ETM.rejectedById > 0)
      ) {
        return true
      } else return false
    },
    isEditMode() {
      if (this.editEducationTimeLength == true) return true
      else return false
    },

    isSameUserApproved() {
      if (this.ETM && this.ETM.approvedBySecondaryId && this.ETM.approvedBySecondaryId > 0) {
        return false
      } else if (this.ETM && this.ETM.approvedById && this.ETM.approvedById > 0) {
        if (this.currentUser && this.currentUser.id == this.ETM.approvedById) {
          return true
        }
      }
      return false
    },
    finalEndDate() {
      let fromDate = new Date(this.ETM.fromDate)
      let toDate = new Date(this.ETM.toDate)
      let pausedDates = 0

      /* qualificationTimeModifications replace with new uppehål object */
      if (this.paused && this.paused.length > 0) {
        this.paused.forEach(pause => {
          pausedDates += this.monthDiff(new Date(pause.fromDate), new Date(pause.toDate))
        })
      }

      // If book has an extension, book pauses, add extended months to current `toDate`. 
      if(get(this, 'ETM.actions', '') ===  'extendEducationModalVisible' && get(this, 'ETM.extension.months', null)) {
        if ( this.ETM.bookPause ) {
          let pauseDate = ''

          let pause = this.ETM.bookPause
          pauseDate = this.monthDiff(new Date(pause.startDate), new Date(pause.endDate))
          return toDate.setMonth(toDate.getMonth() + pauseDate + get(this, 'ETM.extension.months', null))
        }

        return toDate.setMonth(toDate.getMonth() + get(this, 'ETM.extension.months', null))
      }

      /* Plus all paused that he has to be added */
      if (get(this, 'basicEducationLength.value', null) && get(this, 'ETM.extension.months', null)) {
        let finalTime = this.basicEducationLength.value + pausedDates + get(this, 'ETM.extension.months', null)
        return fromDate.setMonth(fromDate.getMonth() + finalTime)
      } else {
        return this.ETM.toDate
      }
    },

    isRegistryRole() {
      return this.currentUserRole === 'registry'
    },

    oldEducationLength() {
      let pauseDate = 0

      if ( this.ETM.bookPause ) {
        let pause = this.ETM.bookPause
        pauseDate = this.monthDiff(new Date(pause.startDate), new Date(pause.endDate))
        return this.monthDiff(new Date(this.ETM.fromDate), new Date(this.ETM.toDate)) + pauseDate
      }

      return this.monthDiff(new Date(this.ETM.fromDate), new Date(this.ETM.toDate))
    },
  },

  asyncComputed: {
    canUpdate(){
      return this.canIDo('education', 'update');
    },
  },

  methods: {
    init() {
      // `registry`- role can extend GU max 3 times up to 12 months.
      if(this.isRegistryRole) {
        this.maxExtendCount = 3
        this.maxExtendRange = 12
      }

      this.timesLengths = this.getQualificationLengthItems.filter((item) => {

        if(!get(this, 'ETM.extension.months', null)) return item

        const remainingMonths = this.maxExtendRange - get(this, 'ETM.extension.months', null)
        const availableMonths = (item.value % remainingMonths) === 0

        if(availableMonths && item.value > 0 && item.value <= remainingMonths) return item

      }).reverse()

      if (
        (this.ETM && this.ETM && this.ETM.approved == true) ||
        this.canApprove == false ||
        (this.ETM && this.ETM && this.ETM.rejectedById && this.ETM.rejectedById > 0 || this.ETM.extension.months >= this.maxExtendRange)
      ) {
        this.isDisabled = true
      } else {
        this.isDisabled = false
      }
    },
    monthDiff(d1, d2) {
      var months
      months = (d2.getFullYear() - d1.getFullYear()) * 12
      months -= d1.getMonth()
      months += d2.getMonth()
      return months <= 0 ? 0 : months
    },
    async deleteETM() {
      let apiMethod = 'deleteBasicEducationTimeModification'

      if(this.isExtendEducationModal) apiMethod = 'deleteExtendedEducation'

      const res = await this.$dialog.confirm({
        text: 'Radering går inte att ångra.',
        title: 'Är du säker på att du vill radera denna ändring?',
        actions: [
          {text: 'Avbryt', key: false, icon: '$sl-cancel'},
          {text: 'Radera', key: true, icon: '$sl-delete', outlined: true}
        ]
      })
      if (res !== true) return

      API[apiMethod](this.ETM.id)
        .then(res => {
          this.$events.fire('showInfo', 'Ändring av grundutbildningstid är raderad')

          this.$emit('close')
        })
        .catch(error => {
          console.log('There was an error:', error)
        })
    },
    async deny() {
      let apiMethod = 'denyBasicEducationTimeModification'

      if(this.isExtendEducationModal) apiMethod = 'rejectExtendedEducation'

      const res = await this.$dialog.confirm({
        title: 'Är du säker på att du vill neka denna ändring?',
        actions: [
          {text: 'Avbryt', key: false, icon: '$sl-cancel'},
          {text: 'Neka', key: true, icon: '$sl-dislike', outlined: true}
        ]
      })
      if (res !== true) return

      API[apiMethod](this.ETM.id)
        .then(res => {
          this.$events.fire('showInfo', 'Ändring av grundutbildningstid är nekad.')
          this.ETM.rejectedById = this.currentUser.id
          this.ETM.rejectedAt = new Date()
          this.isDisabled = true
          this.$emit('close')
        })
        .catch(error => {
          console.log('There was an error:', error)
        })
    },
    //update the form and remove all approvements. After saved, needs to be approved by 2 different persons.
    async update() {
      // Check for user input when extending basic education
      if(this.isEditMode && !this.basicEducationLength) {
        return this.$events.fire('showError', 'Formuläret är inte korrekt ifyllt.')
      }

      let apiMethod = 'updateBasicEducationTimeModification'
      let data = this.ETM
      let message = 'Grundutbildningstid sparad.'

      if(this.isExtendEducationModal) {
        apiMethod = 'updateExtendedEducation'

        data = {
          id: get(this, 'ETM.id', null),
          issuerId: this.currentUserId,
          educationId: get(this, 'selectedPerson.educations[0].id', -1),
          distanceEducationProviderId: get(this, 'ETM.extension.distanceEducationProviderId', -1),
          months: get(this, 'ETM.extension.months') ? (get(this, 'ETM.extension.months') + get(this, 'basicEducationLength.value', null)) : get(this, 'basicEducationLength.value', null),
        }

        message = 'Skickad för godkännande.'
      }

      this.ETM.approvedBy = null
      this.ETM.approvedById = null
      this.ETM.approvedAt = null

      if (this.basicEducationLength) {
        this.ETM.newLengthInMonths = this.basicEducationLength.value
      }

      await API.updateExtendedEducation({ data: data })
        .then(res => {
          if(get(this, 'ETM.approvedById', -1) > 0) return this.approve()
          this.close()
          this.$events.fire('showInfo', message)
        })
        .catch(error => {
          console.log('There was an error:', error)
        })
    },
    //needs to be approve by 2 persons. Check first if it is approved by a first person.
    approve() {
      let apiMethod = 'approveBasicEducationTimeModification'

      if(this.isExtendEducationModal) apiMethod = 'approveExtendedEducation'

      API[apiMethod](this.ETM.id, this.selectedPerson.apprenticeships[0].company.id)
        .then(res => {
          this.isDisabled = true
          this.$emit('close')

          if (res.data.approvedBySecondaryId) {
            if (res.data.price) {
              this.$events.fire('showInfo', 'En order för förlängning är skickad till företaget för godkännande.')
            } else {
              this.$events.fire('showInfo', 'Grundutbildningen är förlängd.')
            }
          }
        })
        .catch(error => {
          console.log('There was an error:', error)
          const errorMessage = error.response?.data || 'Tyvärr gick det inte att godkänna förlängningen.'
          this.$events.fire('showError', errorMessage)
        })
    },
    //reset the form and close emit to close the modal
    close() {
      //reset the form with the original value
      this.basicEducationLength = ''
      this.editEducationTimeLength = false

      this.$emit('close')
    }
  }
}
</script>

<style lang="scss" scoped>
</style>
